/**
 * Height to reserve for a section before it is built, so that building it doesn't move
 * anything below.
 *
 * Only ever runs for LazySectionWrapper, whose only consumer is MultiViewLobby - so a
 * section here is always ONE horizontal row, however many games it holds. The row scrolls
 * sideways, so the game count doesn't enter into it.
 *
 * Numbers are measured against real sections rather than derived from CSS: a plain section
 * is ~231px against a 182px row, which leaves the 50px header and nothing else worth
 * counting, and a jackpot one is ~510px, which puts the panel at ~280 including whatever
 * spacing surrounds it.
 */

import { SECTION_NAMES } from '@repo/common';

import { getHorizontalRowHeight } from '../../../shared/components/VirtualizedGrid/hooks/useHorizontalGridDimensions';

interface SectionHeightParams {
  sectionName: string;
  gamesCount: number;
  hasJackpotPanel: boolean;
  viewportWidth: number;
}

/** --header-title-height-multiview */
const HEADER = 50;

/**
 * Measured difference between a plain section and one with a jackpot panel.
 *
 * Stays wrong for two sections: `hasJackpotPanel` is only `!!section.jackpotName`, while
 * whether a panel actually renders is decided in layouts/Section via useJackpotByName
 * (ON-126484). INSTANT and PROGRESSIVE carry a name but render a shorter panel or none,
 * so they are over-reserved by ~130-230px. Fixing that needs the jackpot store here,
 * which is a cross-feature dependency.
 */
const JACKPOT_PANEL = 280;

const MOBILE_MAX_WIDTH = 768;
const TOURNAMENT_HEIGHT = 400;
const TOURNAMENT_HEIGHT_MOBILE = 350;
const EMPTY_FAVORITES = 150;
const EMPTY_FAVORITES_MOBILE = 120;

/** Used when the calculation can't run at all. */
const FALLBACK_HEIGHT = 300;

export function calculateSectionHeight({
  sectionName,
  gamesCount,
  hasJackpotPanel,
  viewportWidth,
}: SectionHeightParams): number {
  const isMobile = viewportWidth <= MOBILE_MAX_WIDTH;

  if (sectionName === SECTION_NAMES.TOURNAMENTS) {
    return isMobile ? TOURNAMENT_HEIGHT_MOBILE : TOURNAMENT_HEIGHT;
  }

  if (sectionName === 'favorites' && gamesCount === 0) {
    return isMobile ? EMPTY_FAVORITES_MOBILE : EMPTY_FAVORITES;
  }

  return (
    HEADER +
    (hasJackpotPanel ? JACKPOT_PANEL : 0) +
    (gamesCount > 0 ? getHorizontalRowHeight(viewportWidth) : 0)
  );
}

export function getFallbackHeight(sectionName: string): number {
  return sectionName === SECTION_NAMES.TOURNAMENTS ? TOURNAMENT_HEIGHT : FALLBACK_HEIGHT;
}
